import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { COMPANY, NAV_LINKS } from "@/lib/sipl-content";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (href) => {
    setOpen(false);
    if (href.startsWith("#")) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-sipl-line shadow-[0_4px_24px_rgba(11,60,93,0.06)]"
          : "bg-white/40 backdrop-blur-md"
      }`}
    >
      <div className="container-x flex items-center justify-between h-16 md:h-20">
        <a
          href="#top"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="flex items-center gap-3"
          data-testid="navbar-logo"
        >
          <div className="w-9 h-9 rounded-md bg-sipl-primary flex items-center justify-center">
            <span className="font-heading font-bold text-white text-lg">S</span>
          </div>
          <div className="leading-tight">
            <div className="font-heading font-semibold text-sipl-primary text-lg tracking-tight">
              {COMPANY.short}
            </div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-sipl-muted hidden sm:block">
              Sunforce Infra
            </div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => goTo(l.href)}
              className="text-sm text-sipl-fg/80 hover:text-sipl-primary transition-colors link-underline"
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
            className="text-sm text-sipl-fg/70 hover:text-sipl-primary flex items-center gap-2"
            data-testid="navbar-phone"
          >
            <Phone className="w-4 h-4" />
            {COMPANY.phone}
          </a>
          <Button
            onClick={() => goTo("#contact")}
            className="bg-sipl-primary hover:bg-sipl-primary700 text-white rounded-md"
            data-testid="navbar-cta-proposal"
          >
            Request Proposal
          </Button>
        </div>

        <button
          className="lg:hidden p-2 text-sipl-primary"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-sipl-line bg-white">
          <div className="container-x py-4 flex flex-col gap-3">
            {NAV_LINKS.map((l) => (
              <button
                key={l.href}
                onClick={() => goTo(l.href)}
                className="text-left py-2 text-sipl-fg/80 hover:text-sipl-primary"
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </button>
            ))}
            <Button
              onClick={() => goTo("#contact")}
              className="bg-sipl-primary hover:bg-sipl-primary700 text-white mt-2"
              data-testid="mobile-cta-proposal"
            >
              Request Proposal
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
